/**
 * SupportHelpCenter - Prepended Help Center block for /support page
 * 
 * Features:
 * - Client-side instant search across articles
 * - Category filter chips
 * - Article viewer in modal
 * - CTAs for Open Chat, Create Ticket
 * 
 * This component is PREPENDED above the existing Support Tickets section.
 * It does NOT replace or modify any existing ticket functionality.
 */
import { useState, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
} from "../components/ui/dialog";
import {
  MagnifyingGlass,
  ChatCircle,
  Ticket,
  BookOpen,
  ArrowRight,
  CheckCircle,
  Warning,
  ArrowSquareOut,
} from "phosphor-react";
import { helpArticles, helpCategories, searchHelpArticles } from "../data/supportHelpArticles";

// Article card component
function ArticleCard({ article, onClick }) {
  return (
    <button
      onClick={() => onClick(article)}
      className="text-left w-full p-4 rounded-lg border border-zinc-200 bg-white hover:border-[#FF6600]/50 hover:shadow-sm transition-all group"
      data-testid={`help-article-${article.slug}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF6600]/10 flex items-center justify-center">
          <BookOpen size={16} className="text-[#FF6600]" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-zinc-900 group-hover:text-[#FF6600] transition-colors line-clamp-1">
            {article.title}
          </h4>
          <p className="text-sm text-zinc-500 mt-1 line-clamp-2">
            {article.summary}
          </p>
          <Badge variant="secondary" className="mt-2 text-xs bg-zinc-100 text-zinc-600">
            {article.category}
          </Badge>
        </div>
        <ArrowRight size={16} className="text-zinc-400 group-hover:text-[#FF6600] flex-shrink-0 mt-1" />
      </div>
    </button>
  );
}

// Article viewer modal - Premium styled shared viewer for all help articles
function ArticleViewer({ article, open, onClose }) {
  // Find related articles from the data - must be before any conditional returns
  const relatedArticleObjects = useMemo(() => {
    if (!article?.relatedArticles?.length) return [];
    return article.relatedArticles
      .map(slug => helpArticles.find(a => a.slug === slug))
      .filter(Boolean);
  }, [article?.relatedArticles]);
  
  if (!article) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-2xl lg:max-w-3xl" 
        data-testid="help-article-modal"
      >
        {/* Header - Fixed, doesn't scroll */}
        <DialogHeader className="p-6 pb-4 border-b border-zinc-100 bg-gradient-to-br from-zinc-50 to-white">
          <div className="flex items-center gap-2 mb-3">
            <Badge 
              variant="secondary" 
              className="px-3 py-1 text-xs font-medium bg-[#FF6600]/10 text-[#FF6600] border border-[#FF6600]/20 rounded-full"
            >
              {article.category}
            </Badge>
          </div>
          <DialogTitle className="text-2xl font-bold text-zinc-900 leading-tight pr-8">
            {article.title}
          </DialogTitle>
        </DialogHeader>
        
        {/* Body - Scrollable content area */}
        <DialogBody className="p-6">
          <div className="space-y-8">
            {/* Summary - Highlighted lead paragraph */}
            <div className="bg-zinc-50 border border-zinc-100 rounded-xl p-5">
              <p className="text-zinc-700 text-base leading-relaxed">
                {article.summary}
              </p>
            </div>
            
            {/* Prerequisites */}
            {article.prerequisites?.length > 0 && (
              <section>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-4">
                  <div className="w-1 h-4 bg-zinc-300 rounded-full" />
                  Prerequisites
                </h4>
                <ul className="space-y-2 pl-1">
                  {article.prerequisites.map((prereq, i) => (
                    <li key={i} className="flex items-start gap-3 text-zinc-600">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-zinc-100 flex items-center justify-center mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                      </span>
                      <span className="text-[15px] leading-relaxed">{prereq}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            
            {/* Step-by-Step Instructions - The hero section */}
            {article.steps?.length > 0 && (
              <section>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-5">
                  <div className="w-1 h-4 bg-[#FF6600] rounded-full" />
                  Step-by-Step Instructions
                </h4>
                <ol className="space-y-4 pl-1">
                  {article.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF6600] text-white text-sm font-bold flex items-center justify-center shadow-sm">
                        {i + 1}
                      </span>
                      <span className="text-zinc-700 text-[15px] leading-relaxed pt-1">
                        {step}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            )}
            
            {/* Expected Behavior - Success state */}
            {article.expectedBehavior?.length > 0 && (
              <section className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-5">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-4">
                  <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                  Expected Behavior
                </h4>
                <ul className="space-y-3">
                  {article.expectedBehavior.map((behavior, i) => (
                    <li key={i} className="flex items-start gap-3 text-emerald-800">
                      <span className="flex-shrink-0 mt-1">
                        <CheckCircle size={16} weight="fill" className="text-emerald-500" />
                      </span>
                      <span className="text-[15px] leading-relaxed">{behavior}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            
            {/* Common Mistakes - Warning state */}
            {article.commonMistakes?.length > 0 && (
              <section className="bg-amber-50/50 border border-amber-100 rounded-xl p-5">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-amber-700 uppercase tracking-wide mb-4">
                  <Warning size={18} weight="fill" className="text-amber-500" />
                  Common Mistakes
                </h4>
                <ul className="space-y-3">
                  {article.commonMistakes.map((mistake, i) => (
                    <li key={i} className="flex items-start gap-3 text-amber-900">
                      <span className="flex-shrink-0 mt-1">
                        <Warning size={16} weight="fill" className="text-amber-500" />
                      </span>
                      <span className="text-[15px] leading-relaxed">{mistake}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            
            {/* Related Articles */}
            {relatedArticleObjects.length > 0 && (
              <section className="pt-4 border-t border-zinc-100">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-4">
                  <BookOpen size={16} className="text-zinc-400" />
                  Related Articles
                </h4>
                <div className="flex flex-wrap gap-2">
                  {relatedArticleObjects.map((related) => (
                    <Badge
                      key={related.slug}
                      variant="outline"
                      className="px-3 py-1.5 text-sm font-medium text-zinc-600 bg-white border-zinc-200 hover:border-[#FF6600] hover:text-[#FF6600] cursor-pointer transition-colors"
                      onClick={() => {
                        // This will be handled by parent - for now just show the title
                      }}
                    >
                      {related.title}
                    </Badge>
                  ))}
                </div>
              </section>
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export default function SupportHelpCenter({ onCreateTicket }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isArticleOpen, setIsArticleOpen] = useState(false);
  
  // Filter and search articles
  const filteredArticles = useMemo(() => {
    let articles = helpArticles;
    
    // Apply category filter first
    if (selectedCategory) {
      articles = articles.filter(a => a.category === selectedCategory);
    }
    
    // Then apply search
    if (searchQuery.trim().length >= 2) {
      return searchHelpArticles(searchQuery, articles);
    }
    
    // Default: show first 6 articles
    return articles.slice(0, 6);
  }, [searchQuery, selectedCategory]);
  
  // Open article modal
  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    setIsArticleOpen(true);
  };
  
  // Open Beacon chat if available
  const handleOpenChat = () => {
    if (window.Beacon) {
      window.Beacon('open');
    } else {
      // Fallback - scroll to ticket section or show message
      console.log('Beacon not available, use ticket form');
    }
  };
  
  // Scroll to ticket section
  const handleCreateTicket = () => {
    if (onCreateTicket) {
      onCreateTicket();
    } else {
      // Fallback: scroll to ticket section
      const ticketSection = document.querySelector('[data-testid="new-ticket-btn"]');
      if (ticketSection) {
        ticketSection.scrollIntoView({ behavior: 'smooth' });
        ticketSection.click();
      }
    }
  };
  
  return (
    <Card className="mb-6" data-testid="support-help-center">
      <CardContent className="pt-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="font-heading text-2xl font-bold text-zinc-900 uppercase tracking-wide">
            Help Center
          </h2>
          <p className="text-zinc-500 mt-2">
            Search help articles before opening a ticket.
          </p>
        </div>
        
        {/* Search */}
        <div className="relative max-w-lg mx-auto mb-6">
          <MagnifyingGlass 
            size={20} 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" 
          />
          <Input
            type="text"
            placeholder="Search help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 border-2 focus:border-[#FF6600] text-base"
            data-testid="help-search-input"
          />
        </div>
        
        {/* Category chips */}
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !selectedCategory
                ? "bg-[#FF6600] text-white"
                : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
            }`}
            data-testid="help-category-all"
          >
            All
          </button>
          {helpCategories.slice(0, 6).map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? "bg-[#FF6600] text-white"
                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
              data-testid={`help-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {category}
            </button>
          ))}
        </div>
        
        {/* Articles grid */}
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {filteredArticles.length > 0 ? (
            filteredArticles.map((article) => (
              <ArticleCard
                key={article.slug}
                article={article}
                onClick={handleArticleClick}
              />
            ))
          ) : (
            <div className="col-span-2 text-center py-8 text-zinc-500">
              <MagnifyingGlass size={32} className="mx-auto mb-2 text-zinc-300" />
              <p>No articles found for "{searchQuery}"</p>
              <p className="text-sm mt-1">Try a different search term or browse categories</p>
            </div>
          )}
        </div>
        
        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4 border-t border-zinc-100">
          <Button
            variant="outline"
            onClick={handleOpenChat}
            className="gap-2"
            data-testid="help-open-chat-btn"
          >
            <ChatCircle size={18} />
            Open Chat
          </Button>
          <Button
            variant="outline"
            onClick={handleCreateTicket}
            className="gap-2"
            data-testid="help-create-ticket-btn"
          >
            <Ticket size={18} />
            Create Support Ticket
          </Button>
          <Button
            variant="ghost"
            onClick={() => window.open('https://docs.freeservicepro.com', '_blank')}
            className="gap-2 text-zinc-500"
            data-testid="help-full-docs-btn"
          >
            View Full Help Center
            <ArrowSquareOut size={16} />
          </Button>
        </div>
      </CardContent>
      
      {/* Article viewer modal */}
      <ArticleViewer
        article={selectedArticle}
        open={isArticleOpen}
        onClose={() => setIsArticleOpen(false)}
      />
    </Card>
  );
}
